import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Col, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Chart from 'chart.js/auto';
import { mapArrayToObjectReducer, groupBy } from '@awp/helpers/Array';
import { getViewableEndDate, shouldShowInvalidData } from '@stores/graphViewPageSlice';
import { GRAPH_DISPLAY_DAYS } from '@awp/constants';
import {
  buildFrostProbeGraphData,
  buildFrostProbeOptions,
  calculateFrostProbeStartDate,
} from '@awp/components/Graphs/Common/FrostProbeHelper';

import { selectData, selectPrecisionResistorObservation } from '@stores/dataVisualizationSlice';
import { getFrostProbeDatasets, defaultBuildOptions } from './Helper';

import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';

import ObservationTimeDropdown from '@awp/components/Graphs/Common/ObservationTime';

Chart.register(zoomPlugin);

const FrostProbe = ({ graphId }) => {
  const [observationTime, setObservationTime] = useState(12);
  const endDate = useSelector(getViewableEndDate);
  const data = useSelector(selectData);
  const showInvalidData = useSelector(shouldShowInvalidData);
  const precisionResistorObservation = useSelector(selectPrecisionResistorObservation);

  const [frostProbeDatasets, setFrostProbeDatasets] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const [graphOptions, setGraphOptions] = useState([defaultBuildOptions]);

  useMemo(() => {
    if (!data) return;
    const fp = getFrostProbeDatasets(data.Datasets, precisionResistorObservation);

    setFrostProbeDatasets(fp);
  }, [data, showInvalidData]);

  useEffect(() => {
    if (!frostProbeDatasets) return;

    const startDate = calculateFrostProbeStartDate(endDate.value, GRAPH_DISPLAY_DAYS);

    //this is an array of layernumber & elevation, we use this later to determine
    // if any layers are missing and fill them so they show as gaps on the graph
    const layers = frostProbeDatasets
      .map((ds) => {
        return {
          layerNumber: ds.SensorLayerNumber,
          elevation: ds.elevation,
        };
      })
      .reduce((acc, cur) => acc.concat(cur), [])
      .filter((val, idx, self) => self.indexOf(val) === idx);

    // take the observation data and add in the elevation, filter by the observation time
    // and finally group the results by TimeStamp
    const filteredFPData = Object.keys(frostProbeDatasets)
      .map((k) => {
        const result = [];
        //check if the dataset has data
        if (frostProbeDatasets[k].Data) {
          frostProbeDatasets[k].Data.filter((v) => new Date(parseInt(v.x)).getHours() == observationTime).forEach(
            (v) => {
              const value = v.Value === 'NAN' ? null : v.Value;
              const y = v.y === 'NAN' ? null : v.y;
              result.push({
                ...v,
                value: value,
                y: y,
                elevation: frostProbeDatasets[k].RelativeElevation,
                sensorLayerNumber: frostProbeDatasets[k].SensorLayerNumber,
              });
            },
          );
        }
        return [k, groupBy(result, 'x')];
      })
      .reduce(mapArrayToObjectReducer, {});

    // transpose our data into it's final form
    // "<timestamp>": { <observation data> }
    let frostProbeObservations = {};

    Object.keys(filteredFPData).map((k) => {
      Object.keys(filteredFPData[k]).map((y) => {
        frostProbeObservations[y] = frostProbeObservations[y] ?? [];
        frostProbeObservations[y].push(filteredFPData[k][y][0]);
      });
    });

    //we have our frost probe data transposed, lets look for missing
    // sensor layers
    let missing = null;

    Object.keys(frostProbeObservations).map((k) => {
      missing = layers.filter(
        (o) =>
          !frostProbeObservations[k].reduce((obj, o) => ((obj[o.sensorLayerNumber] = o.sensorLayerNumber), obj), [])[
            o.layerNumber
          ],
      );

      if (missing.length > 0) {
        missing.map((m) => {
          frostProbeObservations[k].push({
            AssociatedY: null,
            IsInvalid: true,
            Value: null,
            elevation: m.elevation,
            sensorLayerNumber: m.layerNumber,
            x: parseInt(k),
            y: null,
            z: null,
          });
        });
      }
    });

    //almost done with our frostprobe data setup, sort by elevation
    Object.keys(frostProbeObservations).map((k) => {
      frostProbeObservations[k].sort((a, b) =>
        Math.abs(a.elevation) < Math.abs(b.elevation) ? 1 : Math.abs(b.elevation) < Math.abs(a.elevation) ? -1 : 0,
      );
    });

    //reduce down only the days we are set to display
    frostProbeObservations = Object.keys(frostProbeObservations)
      .filter((k) => parseInt(k) < endDate.value && parseInt(k) > startDate)
      .reduce((obj, key) => {
        return Object.assign(obj, {
          [key]: frostProbeObservations[key],
        });
      }, {});

    setGraphData(buildFrostProbeGraphData(frostProbeObservations, showInvalidData));
    setGraphOptions(buildFrostProbeOptions(frostProbeObservations, graphId, showInvalidData, observationTime));
  }, [frostProbeDatasets, endDate, observationTime]);

  useMemo(() => {
    if (!graphOptions || !graphData) return;

    const graphElm = document.getElementById(graphId);

    if (graphElm !== null) {
      const ctx = graphElm.getContext('2d');
      let chart = Chart.getChart(graphId);

      if (chart) {
        chart.data.datasets = graphData;
        chart.options = graphOptions;
        chart.update();
      } else {
        chart = new Chart(ctx, {
          data: {
            datasets: graphData,
          },
          options: graphOptions,
          height: '250px',
        });
      }
    }
  }, [graphData, graphOptions]);

  const SetObservationTime = (obsTime) => async () => {
    setObservationTime(obsTime);
  };

  return (
    <>
      <Form>
        <Form.Row>
          <Col lg={10} className="mb-10" />
          <Col lg={2} className="mb-2" style={{ zIndex: 14 }}>
            <ObservationTimeDropdown ObservationTimeChanged={SetObservationTime} />
          </Col>
        </Form.Row>
      </Form>

      <div style={{ height: '250px' }}>
        <canvas
          id={graphId}
          key={graphId}
          //options={graphOptions}
          data={{
            datasets: graphData,
          }}
          height={250}
        />
      </div>
    </>
  );
};

FrostProbe.propTypes = {
  graphId: PropTypes.any.isRequired,
};

export default FrostProbe;
